import { createTheme, Theme } from '@mui/material/styles';

// ============================================================
// THEME PRESETS
// ============================================================
// To change the app theme, modify the ACTIVE_THEME variable
// at the bottom of this file. Available options:
//
//   'dark'         - Original dark theme (default)
//   'light'        - Clean light theme with white/gray tones
//   'light-warm'   - Warm light theme with soft cream/beige tones
//   'light-ocean'  - Light blue/ocean-inspired theme
//
// Example: const ACTIVE_THEME: ThemeName = 'light-warm';
// ============================================================

export type ThemeName = 'dark' | 'light' | 'light-warm' | 'light-ocean';

const sharedTypography = {
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  h1: { fontWeight: 700 },
  h2: { fontWeight: 700 },
  h3: { fontWeight: 600 },
  h4: { fontWeight: 600 },
  h5: { fontWeight: 600 },
  h6: { fontWeight: 600 },
};

const sharedShape = { borderRadius: 12 };

// ── Dark Theme (Original) ──────────────────────────────────
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#00d4aa', light: '#33ddbb', dark: '#00a888' },
    secondary: { main: '#7c4dff', light: '#b47cff', dark: '#3f1dcb' },
    background: { default: '#0a0e14', paper: '#12171f' },
    success: { main: '#69f0ae' },
    warning: { main: '#ffab40' },
    error: { main: '#ff5252' },
    text: { primary: '#ffffff', secondary: 'rgba(255, 255, 255, 0.7)' },
  },
  typography: sharedTypography,
  shape: sharedShape,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: '#0a0e14' },
        '::-webkit-scrollbar': { width: '8px', height: '8px' },
        '::-webkit-scrollbar-track': { background: '#12171f' },
        '::-webkit-scrollbar-thumb': { background: '#2a3441', borderRadius: '4px' },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600, borderRadius: 8 },
        contained: { boxShadow: 'none', '&:hover': { boxShadow: '0 4px 12px rgba(0, 212, 170, 0.3)' } },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: { backgroundImage: 'none', border: '1px solid rgba(255, 255, 255, 0.08)' },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { backgroundColor: '#12171f', borderRight: '1px solid rgba(255, 255, 255, 0.08)' },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: { backgroundColor: '#12171f', backgroundImage: 'none' },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: { '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: '#00d4aa' } } },
      },
    },
  },
});

// ── Light Theme (Clean) ────────────────────────────────────
const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#00997a', light: '#00b894', dark: '#007a62' },
    secondary: { main: '#6c5ce7', light: '#a29bfe', dark: '#4834d4' },
    background: { default: '#f5f6fa', paper: '#ffffff' },
    success: { main: '#00b894' },
    warning: { main: '#f39c12' },
    error: { main: '#e74c3c' },
    text: { primary: '#2d3436', secondary: '#636e72' },
    divider: 'rgba(0, 0, 0, 0.08)',
  },
  typography: sharedTypography,
  shape: sharedShape,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: '#f5f6fa' },
        '::-webkit-scrollbar': { width: '8px', height: '8px' },
        '::-webkit-scrollbar-track': { background: '#f0f0f0' },
        '::-webkit-scrollbar-thumb': { background: '#c0c0c0', borderRadius: '4px' },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600, borderRadius: 8 },
        contained: { boxShadow: 'none', '&:hover': { boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)' } },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: { backgroundImage: 'none', border: '1px solid #e8e8e8', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { backgroundColor: '#ffffff', borderRight: '1px solid #e8e8e8' },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: { backgroundColor: '#ffffff', backgroundImage: 'none', color: '#2d3436', borderBottom: '1px solid #e8e8e8' },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: { '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: '#00997a' } } },
      },
    },
  },
});

// ── Light Warm Theme ───────────────────────────────────────
const lightWarmTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#e17055', light: '#e8846e', dark: '#c0543d' },
    secondary: { main: '#6c5ce7', light: '#a29bfe', dark: '#4834d4' },
    background: { default: '#faf5f0', paper: '#ffffff' },
    success: { main: '#00b894' },
    warning: { main: '#fdcb6e' },
    error: { main: '#d63031' },
    text: { primary: '#2d3436', secondary: '#7f8c8d' },
    divider: 'rgba(0, 0, 0, 0.06)',
  },
  typography: sharedTypography,
  shape: sharedShape,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: '#faf5f0' },
        '::-webkit-scrollbar': { width: '8px', height: '8px' },
        '::-webkit-scrollbar-track': { background: '#f5ebe0' },
        '::-webkit-scrollbar-thumb': { background: '#d4c4b0', borderRadius: '4px' },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600, borderRadius: 8 },
        contained: { boxShadow: 'none', '&:hover': { boxShadow: '0 4px 12px rgba(225, 112, 85, 0.25)' } },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: { backgroundImage: 'none', border: '1px solid #ede4da', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { backgroundColor: '#fff8f2', borderRight: '1px solid #ede4da' },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: { backgroundColor: '#fff8f2', backgroundImage: 'none', color: '#2d3436', borderBottom: '1px solid #ede4da' },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: { '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: '#e17055' } } },
      },
    },
  },
});

// ── Light Ocean Theme ──────────────────────────────────────
const lightOceanTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#0984e3', light: '#74b9ff', dark: '#0770c2' },
    secondary: { main: '#00cec9', light: '#81ecec', dark: '#00a8a4' },
    background: { default: '#f0f6fc', paper: '#ffffff' },
    success: { main: '#00b894' },
    warning: { main: '#f39c12' },
    error: { main: '#d63031' },
    text: { primary: '#2d3436', secondary: '#636e72' },
    divider: 'rgba(0, 0, 0, 0.08)',
  },
  typography: sharedTypography,
  shape: sharedShape,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: '#f0f6fc' },
        '::-webkit-scrollbar': { width: '8px', height: '8px' },
        '::-webkit-scrollbar-track': { background: '#e8f0fa' },
        '::-webkit-scrollbar-thumb': { background: '#b0c4de', borderRadius: '4px' },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600, borderRadius: 8 },
        contained: { boxShadow: 'none', '&:hover': { boxShadow: '0 4px 12px rgba(9, 132, 227, 0.25)' } },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: { backgroundImage: 'none', border: '1px solid #dce8f5', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { backgroundColor: '#f7faff', borderRight: '1px solid #dce8f5' },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: { backgroundColor: '#f7faff', backgroundImage: 'none', color: '#2d3436', borderBottom: '1px solid #dce8f5' },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: { '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: '#0984e3' } } },
      },
    },
  },
});

// ============================================================
// ACTIVE THEME SELECTION
// ============================================================
// Change this value to switch the entire app theme:
//   'dark'         - Original dark theme
//   'light'        - Clean white/gray light theme
//   'light-warm'   - Warm cream/beige light theme
//   'light-ocean'  - Blue/ocean light theme
// ============================================================
const ACTIVE_THEME: ThemeName = 'light-warm';

const themes: Record<ThemeName, Theme> = {
  'dark': darkTheme,
  'light': lightTheme,
  'light-warm': lightWarmTheme,
  'light-ocean': lightOceanTheme,
};

const theme = themes[ACTIVE_THEME];
export default theme;
